import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  startAfter,
  where,
  doc,
  getDoc 
} from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';

/**
 * Custom hook for fetching and managing social feed data
 * 
 * @param {number} postsPerPage - Number of posts to fetch per page
 * @returns {Object} - Feed data and methods
 */
const useSocialFeed = (postsPerPage = 10) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  /**
   * Fetch initial posts
   */
  const fetchInitialPosts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Create a query to fetch posts, ordered by creation date
      const postsRef = collection(db, 'posts');
      const q = query(
        postsRef,
        orderBy('createdAt', 'desc'),
        limit(postsPerPage)
      );

      const snapshot = await getDocs(q);
      
      // Check if we have more posts
      setHasMore(snapshot.docs.length === postsPerPage);
      
      // Set the last visible document for pagination
      if (snapshot.docs.length > 0) {
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      } else {
        setLastVisible(null);
        setHasMore(false);
      }

      // Transform the documents to include IDs
      const fetchedPosts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      setPosts(fetchedPosts);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load posts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch next batch of posts for infinite scrolling
   */
  const fetchMorePosts = async () => {
    if (!hasMore || loading || !lastVisible) return;
    
    setLoading(true);
    
    try {
      const postsRef = collection(db, 'posts');
      const q = query(
        postsRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastVisible),
        limit(postsPerPage)
      );

      const snapshot = await getDocs(q);
      
      // Check if we have more posts
      setHasMore(snapshot.docs.length === postsPerPage);
      
      // Set the last visible document for next pagination
      if (snapshot.docs.length > 0) {
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      }

      // Transform the documents to include IDs
      const newPosts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Append new posts to existing ones
      setPosts(prevPosts => [...prevPosts, ...newPosts]);
    } catch (err) {
      console.error('Error fetching more posts:', err);
      setError('Failed to load more posts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch posts from a specific user
   * 
   * @param {string} userId - User ID to fetch posts for
   */
  const fetchUserPosts = async (userId) => {
    setLoading(true);
    setError(null);
    
    try {
      const postsRef = collection(db, 'posts');
      const q = query(
        postsRef,
        where('authorId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(postsPerPage)
      );

      const snapshot = await getDocs(q);
      
      // Check if we have more posts
      setHasMore(snapshot.docs.length === postsPerPage);
      
      // Set the last visible document for pagination
      if (snapshot.docs.length > 0) {
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      } else {
        setLastVisible(null);
        setHasMore(false);
      }

      // Transform the documents to include IDs
      const fetchedPosts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      setPosts(fetchedPosts);
    } catch (err) {
      console.error('Error fetching user posts:', err);
      setError('Failed to load posts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Format post timestamp for display
   * 
   * @param {Object} timestamp - Firestore timestamp
   * @returns {string} - Formatted time string
   */
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const postDate = timestamp.toDate();
    const diffInSeconds = Math.floor((now - postDate) / 1000);
    
    // Less than a minute
    if (diffInSeconds < 60) {
      return 'Just now';
    }
    
    // Less than an hour
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    
    // Less than a day
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }
    
    // Less than a week
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }
    
    // Default: show the date
    return postDate.toLocaleDateString();
  };

  // Fetch comments for a specific post
  const fetchPostComments = async (postId) => {
    try {
      const commentsRef = collection(db, `posts/${postId}/comments`);
      const q = query(commentsRef, orderBy('createdAt', 'asc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (err) {
      console.error('Error fetching comments:', err);
      throw new Error('Failed to load comments');
    }
  };

  // Fetch a single post by ID
  const fetchPostById = async (postId) => {
    try {
      const postRef = doc(db, 'posts', postId);
      const postDoc = await getDoc(postRef);
      
      if (postDoc.exists()) {
        return {
          id: postDoc.id,
          ...postDoc.data()
        };
      } else {
        throw new Error('Post not found');
      }
    } catch (err) {
      console.error('Error fetching post:', err);
      throw new Error('Failed to load post');
    }
  };

  // Reset the feed state
  const resetFeed = () => {
    setPosts([]);
    setLastVisible(null);
    setHasMore(true);
    setError(null);
  };

  // Export functions and state
  return {
    posts,
    loading,
    error,
    hasMore,
    fetchInitialPosts,
    fetchMorePosts,
    fetchUserPosts,
    fetchPostComments,
    fetchPostById,
    formatTimestamp,
    resetFeed
  };
};

export default useSocialFeed;